import { Component } from './Component';
import type { FailedPlace, PlaceUse } from '../domain/FailedPlace';
import { AppStore } from '../store/AppStore';
import { createLogger } from '../utils/logger';
import { isWikiTreeId, wikiTreeUrl } from '../utils/WikiTreeId';

const log = createLogger('NeedsReviewPanel');

/** Uses listed per place before "and N more" (the copied text lists them all). */
const MAX_USES_SHOWN = 5;
/** How long the copy button reads "Copied" before it resets. */
const COPIED_RESET_MS = 2000;

const NOT_FOUND_NOTE = 'No map match. Correcting the place name on WikiTree usually fixes it.';
const DOUBLE_CHECK_NOTE =
  "On the map, but the place name has no state or country we recognize, so the location couldn't be confirmed.";

const plural = (n: number): string => (n === 1 ? '' : 's');
const notFoundSummary = (n: number): string => `${n.toString()} place${plural(n)} not found`;
const doubleCheckSummary = (n: number): string =>
  `${n.toString()} place${plural(n)} to double-check`;

/** Most-used first; Array.prototype.sort is stable, so ties keep arrival order. */
function byUses(places: FailedPlace[]): FailedPlace[] {
  return [...places].sort((a, b) => b.uses.length - a.uses.length);
}

/** Plain-text copy of a place list: every place (most-used first) and every use. */
export function placesAsText(title: string, places: FailedPlace[]): string {
  const lines = [title];
  for (const place of byUses(places)) {
    lines.push('', place.raw);
    for (const use of place.uses) {
      const id = isWikiTreeId(use.wikitreeId) ? ` (${use.wikitreeId})` : '';
      const year = use.year !== undefined ? ` ${use.year.toString()}` : '';
      lines.push(`  ${use.name}${id} — ${use.event}${year}`);
    }
  }
  return lines.join('\n');
}

/**
 * Fixed-position panel (top-right) showing geocode progress and two place lists.
 *
 * Hidden when total === 0 and both lists are empty. Rate-limit warning appears only
 * when rateLimitHit === true in the latest GeocodeProgress update.
 *
 * Progress counts placed, not-found, and deferred places, so the bar reaches the end
 * when geocoding finishes. Deferred places (lookup service unavailable) are not
 * listed; a note asks the user to upload again later. ETA is a lower bound (fallback
 * query variants can add requests).
 *
 * "Not found": no acceptable map match. "To double-check": plotted, but the place
 * names no state or country we recognize, so it was not region-checked. Each list has
 * a one-line note and a "Copy list" button that copies every place and every use as
 * plain text. Each place lists who uses it, most-used first, with names linked to
 * WikiTree when the person has a valid WikiTree ID. All GEDCOM strings are set via
 * textContent — never parsed as HTML.
 */
export class NeedsReviewPanel extends Component {
  private progressEl!: HTMLProgressElement;
  private labelEl!: HTMLSpanElement;
  private etaEl!: HTMLSpanElement;
  private rateLimitEl!: HTMLParagraphElement;
  private deferredEl!: HTMLParagraphElement;
  private failuresEl!: HTMLDetailsElement;
  private failuresSummaryEl!: HTMLElement;
  private failureListEl!: HTMLUListElement;
  /** Last rendered failures array — the list is rebuilt only when this reference changes. */
  private renderedFailures: FailedPlace[] | null = null;
  private unverifiedEl!: HTMLDetailsElement;
  private unverifiedSummaryEl!: HTMLElement;
  private unverifiedListEl!: HTMLUListElement;
  private renderedUnverified: FailedPlace[] | null = null;

  render(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'review-panel';
    el.style.display = 'none';
    el.setAttribute('data-testid', 'review-panel');

    el.innerHTML = `
      <div class="geocode-progress">
        <progress data-testid="geocode-progress" value="0" max="0"
                  aria-label="Place geocoding progress"></progress>
        <span class="progress-label" data-testid="progress-label"
              aria-live="polite" aria-atomic="true">0 placed · 0 not found · 0 places</span>
        <span class="geocode-eta" data-testid="geocode-eta"></span>
      </div>
      <p class="rate-limit-warning" role="alert" data-testid="rate-limit-warning"></p>
      <p class="deferred-note" role="status" data-testid="deferred-note"></p>
      <details class="place-list geocode-failures" style="display:none" data-testid="geocode-failures">
        <summary>0 places not found</summary>
        <p class="list-note"></p>
        <button type="button" class="copy-list" data-testid="copy-failures">Copy list</button>
        <ul></ul>
      </details>
      <details class="place-list geocode-unverified" style="display:none" data-testid="geocode-unverified">
        <summary>0 places to double-check</summary>
        <p class="list-note"></p>
        <button type="button" class="copy-list" data-testid="copy-unverified">Copy list</button>
        <ul></ul>
      </details>
    `;

    this.progressEl = el.querySelector('progress')!;
    this.labelEl = el.querySelector<HTMLSpanElement>('.progress-label')!;
    this.etaEl = el.querySelector<HTMLSpanElement>('.geocode-eta')!;
    this.rateLimitEl = el.querySelector<HTMLParagraphElement>('.rate-limit-warning')!;
    this.deferredEl = el.querySelector<HTMLParagraphElement>('.deferred-note')!;
    this.failuresEl = el.querySelector<HTMLDetailsElement>('.geocode-failures')!;
    this.failuresSummaryEl = el.querySelector('.geocode-failures summary')!;
    this.failureListEl = el.querySelector<HTMLUListElement>('.geocode-failures > ul')!;
    this.unverifiedEl = el.querySelector<HTMLDetailsElement>('.geocode-unverified')!;
    this.unverifiedSummaryEl = el.querySelector('.geocode-unverified summary')!;
    this.unverifiedListEl = el.querySelector<HTMLUListElement>('.geocode-unverified > ul')!;

    // Static copy — set via textContent like everything else in this panel.
    el.querySelector('.geocode-failures .list-note')!.textContent = NOT_FOUND_NOTE;
    el.querySelector('.geocode-unverified .list-note')!.textContent = DOUBLE_CHECK_NOTE;

    const copyFailures = el.querySelector<HTMLButtonElement>('.geocode-failures .copy-list')!;
    copyFailures.addEventListener('click', () => {
      this._copy(copyFailures, this.failuresSummaryEl.textContent ?? '', this.renderedFailures);
    });
    const copyUnverified = el.querySelector<HTMLButtonElement>('.geocode-unverified .copy-list')!;
    copyUnverified.addEventListener('click', () => {
      this._copy(
        copyUnverified,
        this.unverifiedSummaryEl.textContent ?? '',
        this.renderedUnverified,
      );
    });

    return el;
  }

  mount(parent: HTMLElement): void {
    super.mount(parent);
    this.track(AppStore.subscribe(() => this._update()));
    // Reflect current store state immediately — geocoding may have already
    // started (or even finished) before the panel was mounted.
    this._update();
    log.info('NeedsReviewPanel mounted');
  }

  private _update(): void {
    const { geocodeProgress, geocodeFailures, geocodeUnverified } = AppStore.getState();
    const { resolved, failed, deferred, total, rateLimitHit } = geocodeProgress;

    const hasContent = total > 0 || geocodeFailures.length > 0 || geocodeUnverified.length > 0;
    this.el.style.display = hasContent ? '' : 'none';
    if (!hasContent) {
      return;
    }

    const done = resolved + failed + deferred;

    this.progressEl.max = total;
    this.progressEl.value = done;

    const notLookedUp = deferred > 0 ? `${deferred.toString()} not looked up · ` : '';
    this.labelEl.textContent =
      `${resolved.toString()} placed · ${failed.toString()} not found · ` +
      `${notLookedUp}${total.toString()} places`;

    this.deferredEl.textContent =
      deferred > 0
        ? `${deferred.toString()} place${plural(deferred)} not looked up — the place ` +
          'lookup service was busy. Upload the file again later to finish.'
        : '';

    this.etaEl.textContent =
      total > 0 && done < total ? `at least ~${(total - done).toString()}s remaining` : '';

    // Rate-limit warning — textContent mutation triggers role="alert" announcement
    this.rateLimitEl.textContent = rateLimitHit ? 'Rate limit reached — pausing before retry.' : '';

    if (geocodeFailures !== this.renderedFailures) {
      this.renderedFailures = geocodeFailures;
      this._renderPlaces(
        this.failuresEl,
        this.failuresSummaryEl,
        this.failureListEl,
        geocodeFailures,
        notFoundSummary,
      );
    }
    if (geocodeUnverified !== this.renderedUnverified) {
      this.renderedUnverified = geocodeUnverified;
      this._renderPlaces(
        this.unverifiedEl,
        this.unverifiedSummaryEl,
        this.unverifiedListEl,
        geocodeUnverified,
        doubleCheckSummary,
      );
    }
  }

  /** One collapsible list of places (not found, or to double-check), each with who uses it. */
  private _renderPlaces(
    detailsEl: HTMLDetailsElement,
    summaryEl: HTMLElement,
    listEl: HTMLUListElement,
    places: FailedPlace[],
    summary: (count: number) => string,
  ): void {
    const count = places.length;
    detailsEl.style.display = count > 0 ? '' : 'none';
    summaryEl.textContent = summary(count);

    listEl.replaceChildren();
    for (const place of byUses(places)) {
      const li = document.createElement('li');
      li.className = 'failed-place';

      const placeEl = document.createElement('span');
      placeEl.className = 'failed-place-name';
      placeEl.textContent = place.raw;
      li.appendChild(placeEl);

      if (place.uses.length > 0) {
        const usesEl = document.createElement('ul');
        usesEl.className = 'place-uses';
        for (const use of place.uses.slice(0, MAX_USES_SHOWN)) {
          usesEl.appendChild(this._useItem(use));
        }
        const hidden = place.uses.length - MAX_USES_SHOWN;
        if (hidden > 0) {
          const more = document.createElement('li');
          more.className = 'place-uses-more';
          more.textContent = `and ${hidden.toString()} more`;
          usesEl.appendChild(more);
        }
        li.appendChild(usesEl);
      }

      listEl.appendChild(li);
    }
  }

  /** Copy a list as plain text; the button shows "Copied" or "Copy failed". */
  private _copy(button: HTMLButtonElement, title: string, places: FailedPlace[] | null): void {
    const clipboard = navigator.clipboard as Clipboard | undefined;
    if (clipboard === undefined) {
      log.warn('Clipboard unavailable — place list not copied');
      button.textContent = 'Copy failed';
      return;
    }
    clipboard.writeText(placesAsText(title, places ?? [])).then(
      () => {
        button.textContent = 'Copied';
        setTimeout(() => {
          button.textContent = 'Copy list';
        }, COPIED_RESET_MS);
      },
      (err: unknown) => {
        log.warn('Copy to clipboard failed', err);
        button.textContent = 'Copy failed';
      },
    );
  }

  /** "{name} — {event} {year}", name linked to WikiTree only for a valid ID. */
  private _useItem(use: PlaceUse): HTMLLIElement {
    const li = document.createElement('li');

    let nameEl: HTMLElement;
    if (isWikiTreeId(use.wikitreeId)) {
      const a = document.createElement('a');
      a.href = wikiTreeUrl(use.wikitreeId);
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      nameEl = a;
    } else {
      nameEl = document.createElement('span');
    }
    nameEl.textContent = use.name;
    li.appendChild(nameEl);

    const year = use.year !== undefined ? ` ${use.year.toString()}` : '';
    li.appendChild(document.createTextNode(` — ${use.event}${year}`));
    return li;
  }
}
